/**
 * Notifications service — live from W11.
 *
 * Live contract (`backend/src/lexentiqa/api/routers/notifications.py`):
 *
 *   GET    /notifications?unread=&kind=&locale=&limit=&offset=   → Page<Notification>
 *   GET    /notifications/unread                                  → { unread, by_kind, latest_id }
 *   POST   /notifications/:id/read                                → 204
 *   POST   /notifications/read-all?kind=                          → 204
 *   DELETE /notifications/:id                                     → 204
 *   GET    /notifications/preferences                             → Preference[] (the full matrix)
 *   PUT    /notifications/preferences   [{ kind, channel, enabled }] → Preference[]
 *   GET    /notifications/stream        text/event-stream: `event: unread` + UnreadOut
 *
 * Every row is the caller's own — RLS, not a filter here. The list asks for the
 * reader's *current* language (`locale=`), so the bell follows the language
 * switcher at once; an email follows the language stored on the account.
 *
 * The mock branch keeps an in-memory copy of the fixture and answers every
 * call the way the API would — filters, paging, read state, deletion, the
 * unread counts — so a page developed against it cannot look right and ship
 * with a tab that filters nothing.
 */
import { apiResolve, http, HttpError } from '@/api'
import { isLive } from '@/api/config'
import { followEventStream } from '@/api/sse'
import { activeLocale } from '@/i18n'
import {
  NOTIFICATION_CHANNELS,
  NOTIFICATION_KINDS,
  kindsFor,
  notificationLook,
  safeLink,
} from '@/entities/notification'
import type { Notification, NotificationChannel, NotificationKind } from '@/entities/notification'
import fixture from '@/mock/notifications.json'
import mockAuth from '@/mock/auth.json'
import mockUsers from '@/mock/users.json'
import type { Paged } from './clients.service'
import type { ServiceOptions } from './types'

const LIVE = () => isLive('notifications')

/* ═════════════════════════════════════════════════════════ API shapes */

export interface ApiNotification {
  id: number
  kind: string
  title: string
  body: string
  link: string | null
  payload: Record<string, unknown>
  read_at: string | null
  created_at: string
}

export interface ApiUnread {
  unread: number
  by_kind: Record<string, number>
  latest_id: number | null
}

export interface ApiPreference {
  kind: NotificationKind
  channel: NotificationChannel
  enabled: boolean
  /** False when the person never chose and the platform default (on) applies. */
  explicit: boolean
}

/* ═════════════════════════════════════════════════════════ SPA shapes */

export interface NotificationFilters {
  /** true = unread only; false = read only; null = both. */
  unread?: boolean | null
  kind?: NotificationKind | null
  limit?: number
  offset?: number
}

export interface UnreadState {
  total: number
  byKind: Partial<Record<NotificationKind, number>>
  latestId: number | null
}

export const EMPTY_UNREAD: UnreadState = { total: 0, byKind: {}, latestId: null }

export interface Preference {
  kind: NotificationKind
  channel: NotificationChannel
  enabled: boolean
  explicit: boolean
}

/* ═════════════════════════════════════════════════════════ adapters */

export function notificationFromApi(n: ApiNotification): Notification {
  const look = notificationLook(n.kind)
  return {
    id: n.id,
    kind: n.kind,
    title: n.title ?? '',
    body: n.body ?? '',
    /* `link` is lifted from the payload by the API; fall back to the payload
     * itself so an older response still leads somewhere. */
    to: safeLink(n.link ?? n.payload?.link),
    unread: n.read_at == null,
    readAt: n.read_at,
    createdAt: n.created_at,
    icon: look.icon,
    tone: look.tone,
  }
}

export function unreadFromApi(u: ApiUnread | null | undefined): UnreadState {
  if (!u) return { ...EMPTY_UNREAD }
  return {
    total: Number(u.unread ?? 0),
    byKind: { ...(u.by_kind ?? {}) },
    latestId: u.latest_id ?? null,
  }
}

export function preferencesFromApi(rows: ApiPreference[] | null | undefined): Preference[] {
  return (rows ?? []).map((p) => ({
    kind: p.kind,
    channel: p.channel,
    enabled: !!p.enabled,
    explicit: !!p.explicit,
  }))
}

/**
 * The list's query string. `unread=false` is a real filter (read rows only),
 * so it is sent as the string "false" rather than dropped — the HTTP client
 * drops only null and ''.
 */
export function toParams(filters: NotificationFilters = {}): Record<string, unknown> {
  const params: Record<string, unknown> = {
    limit: filters.limit ?? 25,
    offset: filters.offset ?? 0,
    locale: activeLocale(),
  }
  if (filters.unread === true || filters.unread === false) params.unread = String(filters.unread)
  if (filters.kind) params.kind = filters.kind
  return params
}

/* ═════════════════════════════════════════════════════════ mock */

type MockRow = ApiNotification

let mockDb: MockRow[] | null = null
let mockPrefs: Map<string, boolean> | null = null

function db(): MockRow[] {
  if (!mockDb) {
    const now = Date.now()
    /* The dispatcher never writes a viewer a match or a deadline row. */
    const me = mockUsers.find((u) => u.id === mockAuth.currentUserId) as
      | { userKind?: string }
      | undefined
    const kinds: readonly string[] = kindsFor(me?.userKind)
    mockDb = fixture.rows
      .filter((r) => kinds.includes(r.kind))
      .map((r) => {
        const created = new Date(now - r.minutes_ago * 60_000).toISOString()
        return {
          id: r.id,
          kind: r.kind,
          title: r.title,
          body: r.body,
          link: ((r.payload as Record<string, unknown>).link as string | undefined) ?? null,
          payload: r.payload as Record<string, unknown>,
          read_at: r.read ? created : null,
          created_at: created,
        }
      })
  }
  return mockDb
}

function prefs(): Map<string, boolean> {
  if (!mockPrefs) mockPrefs = new Map()
  return mockPrefs
}

/** Test hook: put the mock bell back to its fixture state. */
export function resetMockNotifications(): void {
  mockDb = null
  mockPrefs = null
}

function mockUnread(): ApiUnread {
  const byKind: Record<string, number> = {}
  for (const r of db()) if (!r.read_at) byKind[r.kind] = (byKind[r.kind] ?? 0) + 1
  const ids = db().map((r) => r.id)
  return {
    unread: Object.values(byKind).reduce((a, b) => a + b, 0),
    by_kind: byKind,
    latest_id: ids.length ? Math.max(...ids) : null,
  }
}

function mockList(params: Record<string, unknown>): Paged<ApiNotification> {
  let rows = [...db()].sort((a, b) => b.created_at.localeCompare(a.created_at) || b.id - a.id)
  if (params.unread === 'true') rows = rows.filter((r) => !r.read_at)
  if (params.unread === 'false') rows = rows.filter((r) => r.read_at)
  if (params.kind) rows = rows.filter((r) => r.kind === params.kind)
  const limit = Number(params.limit ?? 25)
  const offset = Number(params.offset ?? 0)
  return { items: rows.slice(offset, offset + limit), total: rows.length, limit, offset }
}

function mockRow(id: number): MockRow {
  const row = db().find((r) => r.id === id)
  if (!row) throw new HttpError(404, 'Notification not found')
  return row
}

function mockPreferences(): ApiPreference[] {
  const stored = prefs()
  return NOTIFICATION_KINDS.flatMap((kind) =>
    NOTIFICATION_CHANNELS.map((channel) => {
      const key = `${kind}:${channel}`
      return {
        kind,
        channel,
        enabled: stored.has(key) ? stored.get(key)! : true,
        explicit: stored.has(key),
      }
    })
  )
}

const opts = (signal: AbortSignal | null | undefined, path: string, method = 'GET') => ({
  signal,
  path,
  method,
})

/* ═════════════════════════════════════════════════════════ the service */

const service = {
  async list(
    filters: NotificationFilters = {},
    { signal }: ServiceOptions = {}
  ): Promise<Paged<Notification>> {
    const params = toParams(filters)
    if (!LIVE()) {
      const page = await apiResolve(mockList(params), opts(signal, '/notifications'))
      return { ...page, items: page.items.map(notificationFromApi) }
    }
    const page: Paged<ApiNotification> = await http.get('/notifications', { params, signal })
    return { ...page, items: (page.items ?? []).map(notificationFromApi) }
  },

  async unread({ signal }: ServiceOptions = {}): Promise<UnreadState> {
    if (!LIVE()) {
      return unreadFromApi(await apiResolve(mockUnread(), opts(signal, '/notifications/unread')))
    }
    return unreadFromApi(await http.get('/notifications/unread', { signal }))
  },

  async markRead(id: number, { signal }: ServiceOptions = {}): Promise<void> {
    if (!LIVE()) {
      await apiResolve(null, opts(signal, `/notifications/${id}/read`, 'POST'))
      const row = mockRow(id)
      row.read_at ??= new Date().toISOString()
      return
    }
    await http.post(`/notifications/${id}/read`, undefined, { signal })
  },

  async markAllRead(
    kind: NotificationKind | null = null,
    { signal }: ServiceOptions = {}
  ): Promise<void> {
    const params = kind ? { kind } : null
    if (!LIVE()) {
      await apiResolve(null, opts(signal, '/notifications/read-all', 'POST'))
      const now = new Date().toISOString()
      for (const r of db()) if (!r.read_at && (!kind || r.kind === kind)) r.read_at = now
      return
    }
    await http.post('/notifications/read-all', undefined, { params, signal })
  },

  async remove(id: number, { signal }: ServiceOptions = {}): Promise<void> {
    if (!LIVE()) {
      await apiResolve(null, opts(signal, `/notifications/${id}`, 'DELETE'))
      mockRow(id)
      mockDb = db().filter((r) => r.id !== id)
      return
    }
    await http.delete(`/notifications/${id}`, { signal })
  },

  async preferences({ signal }: ServiceOptions = {}): Promise<Preference[]> {
    if (!LIVE()) {
      return preferencesFromApi(
        await apiResolve(mockPreferences(), opts(signal, '/notifications/preferences'))
      )
    }
    return preferencesFromApi(await http.get('/notifications/preferences', { signal }))
  },

  /**
   * Save some cells of the matrix. The API writes only the cells it is sent
   * and answers with the whole matrix, which replaces the page's copy — so a
   * cell another tab changed meanwhile is shown as it now is.
   */
  async savePreferences(
    changes: Pick<Preference, 'kind' | 'channel' | 'enabled'>[],
    { signal }: ServiceOptions = {}
  ): Promise<Preference[]> {
    const body = changes.map(({ kind, channel, enabled }) => ({ kind, channel, enabled }))
    if (!LIVE()) {
      await apiResolve(null, opts(signal, '/notifications/preferences', 'PUT'))
      for (const c of body) prefs().set(`${c.kind}:${c.channel}`, c.enabled)
      return preferencesFromApi(mockPreferences())
    }
    return preferencesFromApi(await http.put('/notifications/preferences', body, { signal }))
  },

  /**
   * Follow the live unread count until `signal` aborts.
   *
   * Before every (re)connect it reads `/notifications/unread` through the
   * authenticated client — that renews a token about to expire and catches up
   * on whatever arrived while the stream was down. Mock mode has no stream:
   * the count is read once and changes only with what this tab does.
   */
  async follow(
    onUnread: (state: UnreadState) => void,
    { signal }: { signal: AbortSignal }
  ): Promise<void> {
    if (!LIVE()) {
      onUnread(await service.unread({ signal }))
      return
    }
    await followEventStream({
      path: '/notifications/stream',
      signal,
      before: async () => onUnread(await service.unread({ signal })),
      onEvent: (event) => {
        if (event.event !== 'unread') return
        try {
          onUnread(unreadFromApi(JSON.parse(event.data) as ApiUnread))
        } catch {
          /* A malformed frame is skipped; the next one carries the full state. */
        }
      },
    })
  },
}

export default service
